import React, { useEffect, useState } from 'react';
import { api } from '../hooks/useApi';
import { formatCurrency, formatDate } from '../lib/utils';
import { mxnTotal } from '../lib/payLinks';
import {
  bookingFacts, editionsFromLines, linesFromInvoice, paymentMethodLabel, scheduleLabel, taxLabel,
} from '../lib/invoiceDetails';

// Expanded row under an invoice in Accounting → Invoice History: everything
// the invoice bills, without opening the PDF. Amounts come from the invoice
// itself and show immediately; booking details and receipts load on open.

const Meta = ({ label, children, testId }) => (
  <div data-testid={testId}>
    <p className="text-[10px] uppercase tracking-wide text-[#64748B]">{label}</p>
    <div className="text-sm text-[#F8FAFC] break-words">{children || <span className="text-[#64748B]">—</span>}</div>
  </div>
);

export const InvoiceDetailsRow = ({ invoice: inv, colSpan, multiCurrency }) => {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setFailed(false);
    api.get(`/invoices/${inv.id}/details`)
      .then((d) => { if (alive) setDetails(d); })
      .catch(() => { if (alive) setFailed(true); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [inv.id]);

  const lines = (details && details.lines) || linesFromInvoice(inv);
  const editions = (details && details.editions) || editionsFromLines(lines);
  const receipts = (details && details.receipts) || [];
  const rate = Number(inv.exchange_rate);
  const showMxn = multiCurrency && rate > 0;
  const cc = (inv.advertiser_email_cc || []).filter(Boolean);
  const th = 'py-2 px-3 text-[10px] font-medium tracking-wide uppercase text-[#64748B]';

  return (
    <tr className="bg-[#0A0A0A]" data-testid={`invoice-details-${inv.id}`}>
      <td colSpan={colSpan} className="px-4 pt-3 pb-5 border-l-2 border-[#3B82F6]">
        {/* Invoice facts */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3 mb-4">
          <Meta label="Bill to">
            <div>{inv.advertiser_name}</div>
            {inv.advertiser_email && <div className="text-xs text-[#94A3B8]">{inv.advertiser_email}</div>}
            {cc.length > 0 && <div className="text-xs text-[#94A3B8]">CC: {cc.join(', ')}</div>}
          </Meta>
          <Meta label="Created">
            {formatDate(inv.created_at)}{inv.created_by ? <span className="text-xs text-[#94A3B8]"> by {inv.created_by}</span> : null}
          </Meta>
          <Meta label={editions.length === 1 ? 'Edition' : 'Editions'} testId={`invoice-details-editions-${inv.id}`}>
            {editions.length ? editions.join(', ') : null}
          </Meta>
          <Meta label="Status">
            <span className="capitalize">{inv.status}</span>
            {inv.sent_at && <div className="text-xs text-[#94A3B8]">Emailed {formatDate(inv.sent_at)}</div>}
            {inv.paid_at && <div className="text-xs text-[#94A3B8]">Paid {formatDate(inv.paid_at)}</div>}
          </Meta>
        </div>

        {/* Billed lines */}
        <div className="overflow-x-auto border border-[#1E293B] rounded-md">
          <table className="w-full">
            <thead>
              <tr className="bg-[#121212] border-b border-[#1E293B]">
                <th className={`${th} text-left w-8`}>#</th>
                <th className={`${th} text-left`}>Edition / Runs</th>
                <th className={`${th} text-left`}>Product</th>
                <th className={`${th} text-left`}>Booking details</th>
                <th className={`${th} text-right`}>Rate</th>
                <th className={`${th} text-right`}>Tax</th>
                <th className={`${th} text-right`}>Line total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1E293B]">
              {lines.map((l, i) => {
                const facts = bookingFacts(l);
                const when = scheduleLabel(l);
                const discount = l.booking && l.booking.discount > 0 ? l.booking.discount : 0;
                return (
                  <tr key={l.booking_id || i} className="align-top" data-testid={`invoice-line-${inv.id}-${i}`}>
                    <td className="py-2 px-3 text-xs text-[#64748B]">{i + 1}</td>
                    <td className="py-2 px-3 text-sm text-[#F8FAFC] min-w-[120px]">
                      {l.edition_name || 'N/A'}
                      {when && <div className="text-xs text-[#94A3B8]">{when}</div>}
                    </td>
                    <td className="py-2 px-3 text-sm text-[#F8FAFC]">{l.product_type}</td>
                    <td className="py-2 px-3 text-xs text-[#94A3B8] min-w-[220px] max-w-md">
                      {facts.length > 0 && <div>{facts.join(' · ')}</div>}
                      {l.special_instructions && <div className="italic mt-0.5 whitespace-pre-line">{l.special_instructions}</div>}
                      {l.booking_missing && <div className="text-orange-400 mt-0.5">Booking has since been deleted</div>}
                      {!facts.length && !l.special_instructions && !l.booking_missing && (loading ? <span className="text-[#64748B]">Loading…</span> : <span className="text-[#64748B]">—</span>)}
                    </td>
                    <td className="py-2 px-3 text-sm text-[#F8FAFC] text-right whitespace-nowrap">
                      {formatCurrency(l.rate || 0)}
                      {discount > 0 && (
                        <div className="text-[10px] text-[#94A3B8]">List {formatCurrency(l.booking.list_rate)} − {formatCurrency(discount)} disc.</div>
                      )}
                    </td>
                    <td className="py-2 px-3 text-sm text-[#94A3B8] text-right whitespace-nowrap">
                      {formatCurrency(l.tax_amount || 0)}
                      <div className="text-[10px]">{taxLabel(l)}</div>
                    </td>
                    <td className="py-2 px-3 text-sm font-medium text-[#F8FAFC] text-right whitespace-nowrap">{formatCurrency(l.line_total || 0)}</td>
                  </tr>
                );
              })}
              {lines.length === 0 && (
                <tr><td colSpan={7} className="py-3 px-3 text-sm text-[#64748B]">This invoice has no line items.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col md:flex-row md:justify-between gap-4 mt-4">
          {/* Payments */}
          <div className="text-sm min-w-0" data-testid={`invoice-details-payments-${inv.id}`}>
            <p className="text-[10px] uppercase tracking-wide text-[#64748B] mb-1">Payments</p>
            {inv.paid_currency && (
              <p className="text-[#F8FAFC]">
                Stripe: {formatCurrency(inv.paid_amount_original || 0)} {inv.paid_currency}
                {inv.paid_currency === 'MXN' && inv.stripe_amount_paid ? <span className="text-[#94A3B8]"> (= {formatCurrency(inv.stripe_amount_paid)} USD)</span> : null}
              </p>
            )}
            {receipts.map((r) => (
              <p key={r.id} className="text-[#F8FAFC]">
                <span className="font-mono text-[#3B82F6]">{r.receipt_number}</span>{' '}
                {formatDate(r.payment_date)} · {paymentMethodLabel(r.payment_method)} · {formatCurrency(r.amount_paid)} {r.currency}
                {r.invoice_count > 1 && r.amount_applied != null && (
                  <span className="text-[#94A3B8]"> ({formatCurrency(r.amount_applied)} {r.currency} applied here, {r.invoice_count} invoices)</span>
                )}
              </p>
            ))}
            {!inv.paid_currency && receipts.length === 0 && (
              <p className="text-[#64748B]">{loading ? 'Loading…' : 'No payments recorded'}</p>
            )}
            {failed && <p className="text-xs text-orange-400 mt-1">Booking details could not be loaded; amounts shown are from the invoice.</p>}
            {details && details.totals_match === false && (
              <p className="text-xs text-orange-400 mt-1">Line totals add up to {formatCurrency(details.lines_total)}, not the invoice total.</p>
            )}
          </div>

          {/* Totals */}
          <div className="w-full md:w-72 shrink-0 text-sm space-y-1" data-testid={`invoice-details-totals-${inv.id}`}>
            <div className="flex justify-between"><span className="text-[#94A3B8]">Subtotal ({lines.length} item{lines.length === 1 ? '' : 's'})</span><span className="text-[#F8FAFC]">{formatCurrency(inv.subtotal || 0)}</span></div>
            <div className="flex justify-between"><span className="text-[#94A3B8]">Tax</span><span className="text-[#F8FAFC]">{formatCurrency(inv.tax_total || 0)}</span></div>
            <div className="flex justify-between border-t border-[#1E293B] pt-1 font-semibold"><span className="text-[#F8FAFC]">Total USD</span><span className="text-[#F8FAFC]">{formatCurrency(inv.total || 0)}</span></div>
            {showMxn && (
              <>
                <div className="flex justify-between text-xs"><span className="text-[#94A3B8]">Exchange rate</span><span className="text-[#94A3B8]">{rate.toFixed(4)} USD/MXN</span></div>
                <div className="flex justify-between"><span className="text-[#94A3B8]">Total MXN</span><span className="text-[#F8FAFC]">{formatCurrency(mxnTotal(inv))}</span></div>
              </>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
};
